import React, { useEffect, useState } from 'react';
import LockPick from 'components/LockPick/LockPick';
import Trending from 'components/Trending/Trending';
import Spinner from 'components/Spinner/Spinner';
import { getLockStats } from 'services/picksApi';
import { listPicks } from 'services/picksApi';
import { BATCH_SIZE, ENABLED_SPORTS } from 'utils';
import { ApiPick, ApiPickLeg } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './Home.scss';

type HomeComponentProps = {
  userGroup: string;
};

// top level Home page component, isolated from routing logic
function Home({ userGroup }: HomeComponentProps) {
  const [lockHitRate, setLockHitRate] = useState<number | null>(null);
  const [allSportResults, setAllSportResults] = useState<ApiPick[]>([]);
  const [trendingPicks, setTrendingPicks] = useState<ApiPickLeg[]>([]);
  const [loading, setLoading] = useState(false);

  // One-shot fetch on mount. Any error (auth missing, network) leaves the
  // dial showing a placeholder rather than blocking the page.
  useEffect(() => {
    let runningHits = 0;
    let runningMisses = 0;
    for (const sport of ENABLED_SPORTS) {
      getLockStats(sport)
        .then((stats) => {
          runningHits += stats.hits;
          runningMisses += stats.misses;
          setLockHitRate(runningHits / (runningHits + runningMisses));
        })
        .catch(() => {
          // Silent — LockPick renders '—' when successRate is null.
        });
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchPicksForSport = async () => {
      const allSportResults: ApiPick[] = [];
      const fetchInner = async () => {
        for (const sport of ENABLED_SPORTS) {
          const res = await listPicks({
            sport: sport,
            riskLevel: 'lock',
            batchSize: BATCH_SIZE,
            pageNum: 1,
          });
          const trendingPicks = res.results.filter((pick) =>
            pick.legs.find((leg) => leg.eqShort > 0),
          );
          allSportResults.push(...trendingPicks);
        }
      };
      await fetchInner();
      if (allSportResults.length > 0) {
        setAllSportResults(allSportResults);
      }
      setLoading(false);
    };
    fetchPicksForSport();
  }, []);

  // top 3 trending picks across all enabled sports, based on eqShort
  useEffect(() => {
    const filteredAthletes = allSportResults
      .flatMap((pick) => pick.legs)
      .filter((leg) => leg.eqShort > 0)
      .reduce((acc: ApiPickLeg[], leg) => {
        if (!acc.find((l) => l.athleteId === leg.athleteId)) {
          acc.push(leg);
        }
        return acc;
      }, [])
      .sort((a, b) => b.eqShort - a.eqShort);
    setTrendingPicks(
      filteredAthletes?.length > 3 ? filteredAthletes.slice(0, 3) : filteredAthletes,
    );
  }, [allSportResults]);

  return (
    <div className="home">
      <div className="home__header">
        {loading && (
          <div className={'pick-detail__loading'}>
            <Spinner />
          </div>
        )}
        <div
          className={`home__trending ${
            userGroup?.toLocaleLowerCase().includes('free') ? 'free' : ''
          }`}
        >
          <Trending legs={trendingPicks} userGroup={userGroup} />
        </div>
        <div>
          {`${t('pages.home.header')}`}
          <span className="home__header--logo">us</span>
        </div>
        <div className={'home__header--tm'}>
          {`${t('app.lockPick.tmPre')} `}
          <span>LOCK</span>
          {`${t('app.lockPick.tmPost')}`}
        </div>
      </div>
      <div className="home__list--title">{`${t('pages.home.listTitle')}`}</div>
      <ol className="home__list">
        <li>{`${t('pages.home.listItemOne')}`}</li>
        <li>{`${t('pages.home.listItemTwo')}`}</li>
        <li>
          {`${t('pages.home.listItemThree')}`}
          <span>Lock Platform</span>
          {` ${t('pages.home.listItemFour')}`}
        </li>
      </ol>
      <div className="home__divider"></div>
      <div className="home__lock">
        <LockPick showInfo successRate={lockHitRate} />
      </div>
      <div className="home__footer">{`${t('app.gettingStarted')}`}</div>
    </div>
  );
}

export default Home;
